/**
 * Mentions & Actions — CRUD against action_items/action_item_events.
 *
 * Reads are plain `.from()` selects (the tables' own select policy —
 * sender_id/recipient_id = the caller — already scopes them correctly,
 * same pattern as fetchEngineers() elsewhere in this app). Every write
 * goes through a security-definer RPC (supabase/migrations/0404) —
 * these tables carry no insert/update/delete policy at all, so a direct
 * `.from().update()` would simply be refused by Postgres.
 */
import { supabase } from "../../lib/supabase";

export type ActionKind = "mention" | "action";
export type ActionStatus = "open" | "acknowledged" | "completed" | "snoozed";
export type ActionPriority = "low" | "normal" | "high";
export type ApprovalStatus = "pending" | "approved" | "rejected";

export interface ActionItem {
  id: number;
  kind: ActionKind;
  status: ActionStatus;
  sender_id: string;
  recipient_id: string;
  entity_type: string;
  entity_id: string;
  job_id: number | null;
  context: Record<string, unknown>;
  title: string;
  message: string | null;
  due_at: string | null;
  priority: ActionPriority | null;
  approval_status: ApprovalStatus | null;
  first_viewed_at: string | null;
  acknowledged_at: string | null;
  completed_at: string | null;
  snoozed_until: string | null;
  last_activity_at: string;
  sender_last_read_at: string | null;
  recipient_last_read_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ActionItemWithSender extends ActionItem {
  sender: { full_name: string } | null;
}

export interface ActionItemWithRecipient extends ActionItem {
  recipient: { full_name: string } | null;
}

export interface ActionItemEvent {
  id: number;
  action_item_id: number;
  event_type: string;
  actor_id: string;
  detail: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface MentionableUser {
  id: string;
  full_name: string;
}

const ROW =
  "id,kind,status,sender_id,recipient_id,entity_type,entity_id,job_id,context,title,message,due_at,priority,approval_status,first_viewed_at,acknowledged_at,completed_at,snoozed_until,last_activity_at,sender_last_read_at,recipient_last_read_at,created_at,updated_at";

// ── People picker ────────────────────────────────────────────────────

export async function fetchMentionableUsers(): Promise<MentionableUser[]> {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, full_name")
    .eq("is_active", true)
    .order("full_name");
  if (error) throw error;
  return (data ?? []) as MentionableUser[];
}

// ── Reads ─────────────────────────────────────────────────────────────

/** Open items assigned to me — feeds MyActionsCard. */
export async function fetchMyActionItems(recipientId: string): Promise<ActionItemWithSender[]> {
  const { data, error } = await supabase
    .from("action_items")
    .select(`${ROW},sender:profiles!action_items_sender_id_fkey(full_name)`)
    .eq("recipient_id", recipientId)
    .neq("status", "completed")
    .order("due_at", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as ActionItemWithSender[];
}

/** Everything I've sent recently, whatever its state — feeds SentActionsCard. */
export async function fetchSentActionItems(senderId: string): Promise<ActionItemWithRecipient[]> {
  const { data, error } = await supabase
    .from("action_items")
    .select(`${ROW},recipient:profiles!action_items_recipient_id_fkey(full_name)`)
    .eq("sender_id", senderId)
    .order("created_at", { ascending: false })
    .limit(30);
  if (error) throw error;
  return (data ?? []) as unknown as ActionItemWithRecipient[];
}

export async function fetchActionItemEvents(actionItemId: number): Promise<ActionItemEvent[]> {
  const { data, error } = await supabase
    .from("action_item_events")
    .select("id,action_item_id,event_type,actor_id,detail,metadata,created_at")
    .eq("action_item_id", actionItemId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []) as ActionItemEvent[];
}

// ── Writes (all RPCs) ─────────────────────────────────────────────────

export interface CreateActionItemInput {
  kind: ActionKind;
  recipientId: string;
  entityType: string;
  entityId: string;
  title: string;
  message?: string | null;
  jobId?: number | null;
  context?: Record<string, unknown>;
  dueAt?: string | null;
  priority?: ActionPriority | null;
  approvalStatus?: "pending" | null;
}

export async function createActionItem(input: CreateActionItemInput): Promise<ActionItem> {
  const { data, error } = await supabase.rpc("action_item_create", {
    p_kind: input.kind,
    p_recipient_id: input.recipientId,
    p_entity_type: input.entityType,
    p_entity_id: input.entityId,
    p_title: input.title,
    p_message: input.message ?? null,
    p_job_id: input.jobId ?? null,
    p_context: input.context ?? {},
    p_due_at: input.dueAt ?? null,
    p_priority: input.priority ?? null,
    p_approval_status: input.approvalStatus ?? null,
  });
  if (error) throw error;
  return data as ActionItem;
}

export async function markSeen(id: number): Promise<void> {
  const { error } = await supabase.rpc("action_item_mark_seen", { p_action_item_id: id });
  if (error) throw error;
}

export async function acknowledgeActionItem(id: number): Promise<ActionItem> {
  const { data, error } = await supabase.rpc("action_item_acknowledge", { p_action_item_id: id });
  if (error) throw error;
  return data as ActionItem;
}

export async function completeActionItem(id: number): Promise<ActionItem> {
  const { data, error } = await supabase.rpc("action_item_complete", { p_action_item_id: id });
  if (error) throw error;
  return data as ActionItem;
}

export async function snoozeActionItem(id: number, until: string): Promise<ActionItem> {
  const { data, error } = await supabase.rpc("action_item_snooze", { p_action_item_id: id, p_until: until });
  if (error) throw error;
  return data as ActionItem;
}

export async function approveActionItem(id: number): Promise<ActionItem> {
  const { data, error } = await supabase.rpc("action_item_approve", { p_action_item_id: id });
  if (error) throw error;
  return data as ActionItem;
}

export async function rejectActionItem(id: number, reason?: string): Promise<ActionItem> {
  const { data, error } = await supabase.rpc("action_item_reject", { p_action_item_id: id, p_reason: reason ?? null });
  if (error) throw error;
  return data as ActionItem;
}

export async function commentOnActionItem(id: number, body: string): Promise<ActionItemEvent> {
  const { data, error } = await supabase.rpc("action_item_comment", { p_action_item_id: id, p_body: body });
  if (error) throw error;
  return data as ActionItemEvent;
}
